import {
  Box,
  Collapse,
  Divider,
  Flex,
  HStack,
  Icon,
  Spinner,
  StackDivider,
  Tag,
  TagLabel,
  Text,
  Tooltip,
  useBreakpointValue,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import AppIdentity from "components/AppIdentity";
import EmptyState from "components/ds/EmptyState";
import { Button } from "components/ds";
import React, { Suspense, useMemo, useState } from "react";
import {
  FiArrowLeft,
  FiCheckCircle,
  FiHelpCircle,
  FiMinusCircle,
  FiUsers,
  FiXCircle,
} from "react-icons/fi";
import { TbDeviceDesktop } from "react-icons/tb";
import { MFAccessReviewWithTotals } from "state/members/accessReviews";
import MFMembers from "state/members/membersState";
import AccountsList from "./AccountsList";
import HelpModal from "./HelpModal";
import { Sun } from "lucide-react";
import Campaigns from "./campaigns";
import {
  Routes,
  Route,
  useLocation,
  useParams,
  useMatch,
} from "react-router-dom";
import ReviewDetail from "./campaigns/ReviewDetail";
import CampaignSidepanel from "./campaigns/CampaignSidepanel";

const MainContent = () => {
  const { isOpen, onToggle } = useDisclosure();

  return (
    <VStack pb="200px" minH="100vh" spacing={0} w="full" align="stretch">
      <Flex
        align="flex-start"
        justify="space-between"
        gap="lg"
        pb="lg"
        w="full"
      >
        <VStack align="flex-start" spacing="2xs">
          <Text
            color="text.body.primary"
            fontFamily="heading"
            fontSize="3xl"
            fontWeight={500}
            lineHeight="1.2"
          >
            Access reviews
          </Text>
          <Text
            color="text.body.tertiary"
            fontSize="sm"
            fontWeight={400}
            lineHeight="1.5"
            maxW="620px"
          >
            Here you will see all the access reviews that you've been assigned
            by your IT or security team.
          </Text>
        </VStack>
        <Button
          variant="subtle"
          tone="neutral"
          size="md"
          leadingIcon={<Icon as={FiHelpCircle} boxSize="16px" />}
          onClick={onToggle}
          style={{ flexShrink: 0 }}
        >
          What is this?
        </Button>
      </Flex>

      {/* Campaigns section */}
      <Campaigns />

      <HelpModal isOpen={isOpen} onClose={onToggle} />
    </VStack>
  );
};

const AccessReviewsWithSidepanel = () => {
  return (
    <>
      <MainContent />
      {/* Wrap sidepanel in its own Suspense boundary to prevent it from
          triggering parent Suspense boundaries during data loading */}
      <Suspense fallback={null}>
        <CampaignSidepanel />
      </Suspense>
    </>
  );
};

const MemberAccessReviews = () => {
  // The review experience (campaign tables, virtualized grids, the sidepanel)
  // needs a wide canvas — there's no usable phone layout. Gate the whole route
  // tree behind a desktop check instead of shipping a broken mobile view.
  const isMobile = useBreakpointValue({ base: true, md: false });
  if (isMobile) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        w="full"
        minH="60vh"
      >
        <EmptyState
          icon={<TbDeviceDesktop />}
          title="Access reviews are only available on desktop"
          body="There's too much detail to review comfortably on a phone. Open this page on a larger screen to review and approve access."
        />
      </Box>
    );
  }
  return (
    <Routes>
      <Route path="/" element={<AccessReviewsWithSidepanel />} />
      <Route
        path="/campaigns/:campaignId"
        element={<AccessReviewsWithSidepanel />}
      />
      <Route path="/reviews/:reviewId" element={<ReviewDetail />} />
    </Routes>
  );
};

const List = () => {
  const reviews = MFMembers.useListAppAccessReviews();
  const [selectedId, setSelectedId] = useState<string>();
  const remaining = useMemo(
    () =>
      reviews.filter(
        (r) =>
          r.review.status === "in_progress" &&
          r.total > r.required + r.removed + r.outOfScope,
      ).length,
    [reviews],
  );

  if (reviews.length === 0) {
    return (
      <VStack pt={4}>
        <Icon as={Sun} boxSize={"36px"} color="gray.500" />
        <Text
          fontSize="md"
          fontWeight="500"
          fontFamily="HubotSans, sans-serif"
          color="gray.600"
        >
          Nothing left to do
        </Text>
        <Text fontSize="sm" color="gray.500">
          You're all caught up on your legacy access reviews
        </Text>
      </VStack>
    );
  }
  return (
    <VStack w="full" align="flex-end" spacing={4} divider={<StackDivider />}>
      {remaining > 0 && (
        <Tag size="sm" colorScheme="blue">
          <TagLabel>{`${remaining} remaining`}</TagLabel>
        </Tag>
      )}
      {reviews
        .filter((r) => (selectedId ? r.id === selectedId : true))
        .map((r) => (
          <ReviewRow
            key={r.id}
            reviewWithTotals={r}
            onSelect={() =>
              selectedId ? setSelectedId(undefined) : setSelectedId(r.id)
            }
          />
        ))}
    </VStack>
  );
};

const ReviewRow = React.memo(
  ({
    reviewWithTotals,
    onSelect,
  }: {
    reviewWithTotals: MFAccessReviewWithTotals;
    onSelect: () => any;
  }) => {
    const review = reviewWithTotals.review;
    const { isOpen, onToggle } = useDisclosure();
    return (
      <>
        <VStack w="full" align="flex-start">
          {isOpen && (
            <Button
              size="sm"
              variant="subtle"
              tone="neutral"
              leadingIcon={<Icon as={FiArrowLeft} />}
              onClick={() => {
                onSelect();
                onToggle();
              }}
            >
              Back
            </Button>
          )}
          <HStack
            w="full"
            justify={"space-between"}
            p={1}
            px={2}
            border="1px solid transparent"
            onClick={() => {
              onToggle();
              onSelect();
            }}
            rounded={4}
            _hover={{
              bg: "gray.50",
              border: "1px solid",
              borderColor: "gray.200",
              cursor: "pointer",
            }}
          >
            <AppIdentity app={review.app} />
            <HStack spacing={4}>
              <Tag
                size="sm"
                colorScheme={
                  review.status === "in_progress"
                    ? "orange"
                    : review.status === "ready_for_approval"
                      ? "blue"
                      : "green"
                }
              >
                <TagLabel textTransform={"capitalize"}>
                  {review.status.replaceAll("_", " ")}
                </TagLabel>
              </Tag>
              <HStack spacing={3}>
                <Tooltip label="Required accounts">
                  <HStack spacing={1}>
                    <Icon as={FiCheckCircle} boxSize="12px" color="green.700" />
                    <Text fontSize="xs" color="gray.600">
                      {reviewWithTotals.required}
                    </Text>
                  </HStack>
                </Tooltip>
                <Tooltip label="Not Required accounts">
                  <HStack spacing={1}>
                    <Icon as={FiXCircle} boxSize="12px" color="red.700" />
                    <Text fontSize="xs" color="gray.600">
                      {reviewWithTotals.removed}
                    </Text>
                  </HStack>
                </Tooltip>
                <Tooltip label="Out of scope">
                  <HStack spacing={1}>
                    <Icon as={FiMinusCircle} boxSize="12px" color="gray.700" />
                    <Text fontSize="xs" color="gray.600">
                      {reviewWithTotals.outOfScope}
                    </Text>
                  </HStack>
                </Tooltip>
                <Tooltip label="Total number">
                  <HStack spacing={1}>
                    <Icon as={FiUsers} boxSize="12px" color="blue.700" />
                    <Text fontSize="xs" color="gray.600">
                      {reviewWithTotals.total}
                    </Text>
                  </HStack>
                </Tooltip>
              </HStack>
            </HStack>
          </HStack>
        </VStack>
        <Collapse in={isOpen} style={{ width: "100%" }}>
          <Suspense fallback={<Spinner />}>
            <Box py={4}>
              {isOpen && (
                <AccountsList accessReviewWithTotal={reviewWithTotals} />
              )}
            </Box>
          </Suspense>
        </Collapse>
      </>
    );
  },
);

export default MemberAccessReviews;
